/**
 * The microphone's samples, brought to the wire's rate and cut into its frames.
 *
 * The phone's recorder is asked for 16 kHz and usually hands that over, so
 * `live-pcm.ts` resamples one whole buffer at a time and is right to. The
 * browser is the other way round: its capture runs at the device's rate (48 kHz
 * on most machines), and the worklet hands over 128 samples a render quantum,
 * batched. Resampling each batch on its own would restart the interpolation at
 * every batch — a click every 21 ms — and round each batch's length, so the
 * stream would drift off the rate it claims. Here the resampler is one stream:
 * it carries its read position and the last sample across calls, so a second
 * of sound in any number of pieces is the same samples as a second in one.
 *
 * Going down, a straight line between neighbours is not enough on its own: a
 * 12 kHz hiss at 48 kHz folds back into the speech band at 16 kHz. A box of
 * `round(from / to)` samples in front of the interpolation takes most of it out
 * (a third of its height at 48 to 16), which is plenty for a recogniser and
 * costs one add and one subtract a sample. Going up, nothing is filtered.
 *
 * The framer collects the resampled samples into the frames the live sockets
 * carry — a tenth of a second, `LIVE_INPUT_RATE / 10` samples — and hands each
 * one over encoded. The encoder is the caller's (the phone's `floatToPcm16`),
 * so the arithmetic of the bytes stays in one place.
 *
 * Pure: arrays in, arrays out, so `pcm-stream.test.ts` proves it without a
 * microphone.
 */

export type StreamResampler = {
  /** The next piece of the input; answers the output samples it completes. */
  readonly push: (input: Float32Array) => Float32Array<ArrayBuffer>;
  /** Forget the stream: the next push starts a new one. */
  readonly reset: () => void;
};

/**
 * One continuous stream from `fromRate` to `toRate`.
 *
 * `position` is where the next output sample falls, in input samples, counted
 * from the first sample this push can read: the carried one, when there is
 * one. An output needs the samples either side of it, so the last input
 * sample of a push is kept for the next and the output that needs the sample
 * after it waits too.
 */
export const createStreamResampler = (fromRate: number, toRate: number): StreamResampler => {
  const same = fromRate === toRate || fromRate <= 0 || toRate <= 0;
  const step = fromRate / toRate;
  /** The box in front of a downsample; 1 is no filter. */
  const taps = step > 1 ? Math.max(1, Math.round(step)) : 1;

  let window = new Float32Array(taps);
  let windowAt = 0;
  let windowSum = 0;
  let primed = false;
  let carry: number | null = null;
  let position = 0;

  /** One input sample through the box: the mean of the last `taps` samples. */
  const smooth = (sample: number): number => {
    if (taps === 1) return sample;
    if (!primed) {
      // The box starts full of the first sample rather than of zeros, so the
      // stream does not open on a ramp up from silence.
      window.fill(sample);
      windowSum = sample * taps;
      primed = true;
    }
    windowSum += sample - (window[windowAt] ?? 0);
    window[windowAt] = sample;
    windowAt = (windowAt + 1) % taps;
    return windowSum / taps;
  };

  const reset = (): void => {
    window = new Float32Array(taps);
    windowAt = 0;
    windowSum = 0;
    primed = false;
    carry = null;
    position = 0;
  };

  const push = (input: Float32Array): Float32Array<ArrayBuffer> => {
    if (same) return Float32Array.from(input);
    if (input.length === 0) return new Float32Array(0);
    const offset = carry === null ? 0 : 1;
    const length = input.length + offset;
    const read = new Float32Array(length);
    if (carry !== null) read[0] = carry;
    for (let at = 0; at < input.length; at += 1) read[at + offset] = smooth(input[at] ?? 0);

    const out: number[] = [];
    while (position + 1 < length) {
      const low = Math.floor(position);
      const between = position - low;
      out.push((read[low] ?? 0) * (1 - between) + (read[low + 1] ?? 0) * between);
      position += step;
    }
    // A position exactly on the last sample needs nothing after it.
    if (position === length - 1) {
      out.push(read[length - 1] ?? 0);
      position += step;
    }
    carry = read[length - 1] ?? 0;
    position -= length - 1;
    return Float32Array.from(out);
  };

  return { push, reset };
};

export type PcmFramerInput = {
  readonly fromRate: number;
  readonly toRate: number;
  /** Samples a frame, at `toRate`. */
  readonly frameSamples: number;
  /** Samples to the wire's bytes: the phone's `floatToPcm16`. */
  readonly encode: (samples: Float32Array) => Uint8Array<ArrayBuffer>;
  readonly onFrame: (pcm: Uint8Array<ArrayBuffer>) => void;
};

export type PcmFramer = {
  /** A piece of the capture, at `fromRate`. Every frame it completes is handed over now. */
  readonly push: (samples: Float32Array) => void;
  /** Hand over what is held as one short frame, if anything is. */
  readonly flush: () => void;
  /** Drop what is held, and start the stream again. */
  readonly reset: () => void;
  /** How many samples are waiting for the rest of their frame. */
  readonly held: () => number;
};

export const createPcmFramer = ({ fromRate, toRate, frameSamples, encode, onFrame }: PcmFramerInput): PcmFramer => {
  const resampler = createStreamResampler(fromRate, toRate);
  const size = Math.max(1, Math.floor(frameSamples));
  let frame = new Float32Array(size);
  let filled = 0;

  const push = (samples: Float32Array): void => {
    const resampled = resampler.push(samples);
    let at = 0;
    while (at < resampled.length) {
      const take = Math.min(size - filled, resampled.length - at);
      frame.set(resampled.subarray(at, at + take), filled);
      filled += take;
      at += take;
      if (filled === size) {
        onFrame(encode(frame));
        frame = new Float32Array(size);
        filled = 0;
      }
    }
  };

  const flush = (): void => {
    if (filled === 0) return;
    onFrame(encode(frame.subarray(0, filled)));
    frame = new Float32Array(size);
    filled = 0;
  };

  const reset = (): void => {
    resampler.reset();
    frame = new Float32Array(size);
    filled = 0;
  };

  return { push, flush, reset, held: () => filled };
};
